<script setup lang="ts">
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import { t } from 'i18next';
import { useField } from 'vee-validate';
import { toRef, ref, watch } from 'vue';
import VeeRadioGroup from '../RadioGroup/VeeRadioGroup.vue';
import { ReferencingObjectDto } from '@/api/generator';
import EntityReferencesModal from '@/components/EntityReferencesModal/EntityReferencesModal.vue';
import { useVeeFormProps } from '../utils';

export interface AvailabilityProps {
  name: string;
  isReferencedBy?: ReferencingObjectDto[] | null;
  hideReferences?: boolean;
  impactMessage?: string;
  label?: string;
  infoMessage?: string;
  disabled?: boolean;
}

const props = withDefaults(defineProps<AvailabilityProps>(), {
  label: () => t('Availability'),
  disabled: false,
  infoMessage: undefined,
  isReferencedBy: null,
  impactMessage: undefined,
  hideReferences: false,
});

const veeProps = useVeeFormProps(props);

const { value } = useField<boolean>(toRef(() => props.name));
const entityReferences = ref<ReferencingObjectDto[] | null>(null);

watch(
  () => value.value,
  async (newValue) => {
    if (newValue === false) {
      entityReferences.value = props.isReferencedBy;
    }
  },
);

const onCancel = () => {
  value.value = true;
  entityReferences.value = null;
};
</script>

<template>
  <div>
    <FormLabel :label="label" :disabled="veeProps.disabled" :infoMessage="infoMessage"></FormLabel>
    <p v-if="veeProps.mode === 'details'" class="availText text-body-1 text-primary-darken-2">
      {{ value ? $t('Enabled') : $t('Disabled') }}
    </p>
    <VeeRadioGroup v-else :name="name" :disabled="veeProps.disabled">
      <v-radio :label="$t('Enabled')" :value="true"></v-radio>
      <v-radio :label="$t('Disabled')" :value="false"></v-radio>
    </VeeRadioGroup>
  </div>
  <EntityReferencesModal
    :hideRefTable="hideReferences"
    :references="entityReferences"
    :title="$t('Confirmation')"
    @dialogOpen="
      (isOpen: boolean) => {
        if (!isOpen && entityReferences) onCancel();
      }
    "
  >
    <template #title>$t('Confirmation')</template>
    <template v-if="impactMessage" #description
      ><p class="text-pre-line">{{ impactMessage }}</p></template
    >
    <template #actions>
      <SecondaryVBtn class="mr-0" @click="onCancel">{{ $t('Cancel') }}</SecondaryVBtn>
      <PrimaryVBtn class="mr-0" @click="entityReferences = null">{{ $t('Disable') }}</PrimaryVBtn>
    </template>
  </EntityReferencesModal>
</template>

<style scoped lang="scss">
.availText {
  min-height: 44px;
}
</style>
